import React from "react";
import { fileUrl } from "../../../api";
import { Trash2, Pencil, MapPin, Clock, Camera, PenLine, Link2, CheckCircle2, PlayCircle, CalendarClock, Users, AlertTriangle, UserRoundCheck } from "lucide-react";
import { LocationEditor, ReportActions } from "./InterventionTools";
import ServiceBadge from "../ServiceBadge";
import GalleryToggle from "./GalleryToggle";

const STATUS = {
  planifiee: { label: "Planifiée", cls: "bg-neutral-200 text-neutral-700", icon: CalendarClock },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-700", icon: PlayCircle },
  terminee: { label: "Terminée", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const fmt = (x) => (x ? new Date(x).toLocaleString("fr-FR") : "—");
const hm = (x) => (x ? new Date(x).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const durStr = (s, e) => {
  if (!s || !e) return "";
  const m = Math.max(0, Math.round((new Date(e) - new Date(s)) / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h${String(m % 60).padStart(2, "0")}` : `${m} min`;
};
const gpsLink = (g) => (g && g.lat ? `https://www.openstreetmap.org/?mlat=${g.lat}&mlon=${g.lng}#map=18/${g.lat}/${g.lng}` : null);

const InterventionCard = ({ it, onRemove, onReload, onCopyLink, onReplace, onEdit }) => {
            const st = STATUS[it.status] || STATUS.planifiee;
            const StIcon = st.icon;
            return (
              <div key={it.id} className={`bg-white border rounded-xl p-5 ${it.needs_reassign ? "border-red-300 ring-1 ring-red-200" : "border-neutral-200"}`} data-testid={`intervention-card-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-neutral-900">{it.client_nom}</h3>
                      {it.service && it.service !== "clean" && <ServiceBadge service={it.service} testid={`intv-service-${it.id}`} />}
                      <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>
                        <StIcon size={12} /> {st.label}
                      </span>
                      {it.needs_reassign && (
                        <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid={`intv-needs-reassign-${it.id}`} title={it.reassign_reason || ""}>
                          <AlertTriangle size={12} /> À réaffecter
                        </span>
                      )}
                      {it.gps_alert && (
                        <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`intv-gpsalert-${it.id}`}>
                          <AlertTriangle size={12} /> Hors zone
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600 mt-1">
                      {it.prestation || "Nettoyage"} · {fmt(it.date)}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {[it.adresse, it.ville].filter(Boolean).join(", ") || "—"}
                    </p>
                    {(it.collaborateurs?.length > 0 || it.collaborateur_nom) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <Users size={13} className="text-neutral-400" />
                        {(it.collaborateurs?.length > 0
                          ? it.collaborateurs.map((c) => c.nom)
                          : [it.collaborateur_nom]
                        ).map((nom) => (
                          <span key={nom} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">{nom}</span>
                        ))}
                      </div>
                    )}
                    {it.needs_reassign && it.reassign_reason && <p className="text-xs text-red-600 mt-1" data-testid={`intv-reassign-reason-${it.id}`}>{it.reassign_reason}</p>}
                    {it.reassignments?.length > 0 && (
                      <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><UserRoundCheck size={12} />
                        {it.reassignments.slice(-1).map((r) => `${r.auto ? "Réaffectation auto" : "Réaffecté"} : ${(r.from || []).join(", ") || "—"} → ${r.to}${r.distance_km != null ? ` (${r.distance_km} km)` : ""} · ${r.reason}`)}
                      </p>
                    )}
                    {it.status !== "terminee" && onReplace && (
                      <button onClick={() => onReplace(it)} data-testid={`intv-replace-${it.id}`} className={`mt-2 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1.5 ${it.needs_reassign ? "bg-red-600 hover:bg-red-700 text-white" : "border border-neutral-300 text-neutral-700 hover:border-brand hover:text-brand"}`}>
                        <UserRoundCheck size={13} /> {it.needs_reassign ? "Affecter un remplaçant" : "Remplacer un easer"}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {onEdit && <button onClick={() => onEdit(it)} className="text-neutral-400 hover:text-brand p-1.5" title="Modifier" data-testid={`intv-edit-${it.id}`}><Pencil size={16} /></button>}
                    <button onClick={() => onRemove(it.id)} className="text-neutral-400 hover:text-red-500 p-1.5" data-testid={`intv-delete-${it.id}`}><Trash2 size={17} /></button>
                  </div>
                </div>

                {(it.start_at || it.end_at) && (
                  <div className="mt-4 border-t border-neutral-100 pt-3 grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-neutral-700">
                      <Clock size={14} className="text-brand" /> Début : <b>{fmt(it.start_at)}</b>
                      {gpsLink(it.start_gps) && <a href={gpsLink(it.start_gps)} target="_blank" rel="noreferrer" className="text-brand hover:underline flex items-center gap-1"><MapPin size={13} /> position</a>}
                    </div>
                    <div className="flex items-center gap-2 text-neutral-700">
                      <Clock size={14} className="text-emerald-600" /> Fin : <b>{fmt(it.end_at)}</b>
                      {gpsLink(it.end_gps) && <a href={gpsLink(it.end_gps)} target="_blank" rel="noreferrer" className="text-brand hover:underline flex items-center gap-1"><MapPin size={13} /> position</a>}
                    </div>
                  </div>
                )}

                {(it.photos_avant?.length > 0 || it.photos_apres?.length > 0 || it.signature) && (
                  <div className="mt-3 flex flex-wrap gap-4">
                    {it.photos_avant?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-neutral-500 mb-1 flex items-center gap-1"><Camera size={12} /> Avant</p>
                        <div className="flex gap-2 flex-wrap">{it.photos_avant.map((p) => (
                          <div key={p} className="w-16"><img src={fileUrl(p)} alt="avant" className="w-16 h-16 object-cover rounded-md border border-neutral-200" />{it.captions?.[p] && <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{it.captions[p]}</p>}</div>
                        ))}</div>
                      </div>
                    )}
                    {it.photos_apres?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-neutral-500 mb-1 flex items-center gap-1"><Camera size={12} /> Après</p>
                        <div className="flex gap-2 flex-wrap">{it.photos_apres.map((p) => (
                          <div key={p} className="w-16"><img src={fileUrl(p)} alt="après" className="w-16 h-16 object-cover rounded-md border border-neutral-200" />{it.captions?.[p] && <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{it.captions[p]}</p>}</div>
                        ))}</div>
                      </div>
                    )}
                    {it.signature && (
                      <div>
                        <p className="text-xs font-semibold text-neutral-500 mb-1 flex items-center gap-1"><PenLine size={12} /> Signature</p>
                        <img src={fileUrl(it.signature)} alt="signature" className="h-16 rounded-md border border-neutral-200 bg-white" />
                      </div>
                    )}
                  </div>
                )}

                {it.gps_alert && it.gps_alerts?.length > 0 && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3" data-testid={`intv-gpsalert-detail-${it.id}`}>
                    <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertTriangle size={14} /> Pointage(s) hors zone</p>
                    {it.gps_alerts.map((a) => (
                      <p key={`${a.phase}-${a.at}-${a.by}`} className="text-xs text-red-600 mt-1">
                        {a.phase === "start" ? "Début" : "Fin"} à <b>{a.distance_m} m</b> de l'adresse prévue (seuil {a.radius_m} m){a.by ? ` · ${a.by}` : ""}
                        {a.gps && <> · <a href={`https://www.openstreetmap.org/?mlat=${a.gps.lat}&mlon=${a.gps.lng}#map=18/${a.gps.lat}/${a.gps.lng}`} target="_blank" rel="noreferrer" className="underline">voir</a></>}
                      </p>
                    ))}
                  </div>
                )}

                {it.status !== "terminee" && <LocationEditor it={it} onReload={onReload} />}

                {it.pointages?.length > 0 && (
                  <div className="mt-3 border-t border-neutral-100 pt-3" data-testid={`intv-pointages-${it.id}`}>
                    <p className="text-xs font-semibold text-neutral-500 mb-1.5 flex items-center gap-1"><Users size={12} /> Heures par intervenant</p>
                    <div className="space-y-1">
                      {it.pointages.map((p) => (
                        <div key={`${p.login}-${p.start_at}`} className="text-sm text-neutral-600 flex flex-wrap items-center gap-x-3">
                          <span className="font-medium text-neutral-800 min-w-[90px]">{p.nom || p.login}</span>
                          <span><Clock size={12} className="inline mr-1 text-brand" />{hm(p.start_at)} → {p.end_at ? hm(p.end_at) : "en cours"}</span>
                          {p.end_at && <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">{durStr(p.start_at, p.end_at)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {it.tasks?.length > 0 && (
                  <div className="mt-3 border-t border-neutral-100 pt-3" data-testid={`intv-tasks-${it.id}`}>
                    <p className="text-xs font-semibold text-neutral-500 mb-1.5 flex items-center gap-1"><CheckCircle2 size={12} /> Tâches effectuées</p>
                    <div className="flex flex-wrap gap-1.5">
                      {it.tasks.map((t) => (
                        <span key={t} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {it.note && <p className="text-sm text-neutral-500 mt-3 italic">« {it.note} »</p>}

                {it.status === "terminee" && <ReportActions it={it} onReload={onReload} />}
                {it.status === "terminee" && <div className="mt-2 flex items-center gap-3">{it.gallery?.published && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`intv-gallery-badge-${it.id}`}>Dans la galerie</span>}<GalleryToggle it={it} onReload={onReload} /></div>}

                {it.notify_client && (
                  <button onClick={() => onCopyLink(it.share_token)} className="mt-3 text-sm text-brand hover:underline flex items-center gap-1.5" data-testid={`intv-copylink-${it.id}`}>
                    <Link2 size={14} /> Copier le lien de suivi client
                  </button>
                )}
              </div>
            );
};

export default InterventionCard;
